// WHY: ONE list of roles and ONE set of "who may do what to a user" checks,
// shared by AddUser, EditUser and UserManagement. It mirrors the users block of
// firestore.rules (canGiveRole / managesOtherUser / self branch, D-305) so a
// screen never offers something the rules will refuse (LOW-14, MED-29,
// CORE lesson 35). Change this file ONLY together with firestore.rules and
// tests/rules/d305users.rules.test.mjs.

export const ROLE_OPTIONS = [
  { value: "owner",         label: "Owner" },
  { value: "sm",            label: "Service Manager" },
  { value: "manager",       label: "Manager" },
  { value: "site_engineer", label: "Site Engineer" },
  { value: "store",         label: "Store" },
  { value: "accounts",      label: "Accountant" },
  { value: "technician",    label: "Technician" },
];

// = smManagedRoles() in firestore.rules
const SM_MANAGED_ROLES = ["manager", "site_engineer", "store", "technician"];

export function isKnownRole(role) {
  return ROLE_OPTIONS.some((o) => o.value === role);
}

export function roleLabel(role) {
  const found = ROLE_OPTIONS.find((o) => o.value === role);
  if (found) return found.label;
  return role ? `Unknown role (${role})` : "No role";
}

// = canGiveRole() in firestore.rules: the Owner gives all 7 roles, an SM only
// manager / site_engineer / store / technician, everyone else none.
export function rolesCallerMayGive(callerRole) {
  if (callerRole === "owner") return ROLE_OPTIONS;
  if (callerRole === "sm") return ROLE_OPTIONS.filter((o) => SM_MANAGED_ROLES.includes(o.value));
  return [];
}

export function canGiveRole(callerRole, role) {
  return rolesCallerMayGive(callerRole).some((o) => o.value === role);
}

// What the caller may do on this users doc:
//   "self"   - own staff doc (doc id == own Auth uid): name / phone only
//   "manage" - managesOtherUser(): Owner -> any other user;
//              SM -> other users whose role is manager / site_engineer / store / technician
//   "none"   - anything else (incl. a doc that carries the caller's uid under another id)
// caller = { uid, role }   target = { id (doc id), uid, role }
export function editModeFor(caller, target) {
  if (!caller?.uid || !target?.id) return "none";
  const ownDocId = target.id === caller.uid;
  const ownUid = (target.uid || "") === caller.uid;
  if (ownDocId) return "self";
  if (ownUid) return "none";
  if (caller.role === "owner") return "manage";
  if (caller.role === "sm" && SM_MANAGED_ROLES.includes(target.role)) return "manage";
  return "none";
}

// The exact fields EditUser sends. Self: only name + phone + whatsapp_phone (the rules refuse
// role / active / status on your own doc - even ADDING a missing field counts
// as a change). Manage: name, role, phone, active, and status that FOLLOWS the
// Active tick (one source - MED-31), + whatsapp_phone (D-297, contact only, never
// a login field). Both phones must already be normalised.
// Returns { ok: true, payload } or { ok: false, error }.
export function buildUserUpdate({ mode, form, callerRole }) {
  if (mode !== "self" && mode !== "manage") {
    return { ok: false, error: "You cannot edit this user." };
  }
  const name = String(form?.name ?? "").trim();
  if (!name) return { ok: false, error: "Name is required." };
  const phone = form?.phone ?? "";
  const whatsapp_phone = form?.whatsapp_phone ?? "";

  if (mode === "self") return { ok: true, payload: { name, phone, whatsapp_phone } };

  if (!canGiveRole(callerRole, form?.role)) {
    return { ok: false, error: "Pick a role from the list - this role cannot be saved." };
  }
  const active = form?.active === true;
  return {
    ok: true,
    payload: { name, role: form.role, phone, whatsapp_phone, active, status: active ? "active" : "inactive" },
  };
}